/* eslint-disable react/no-array-index-key */

import React, { useEffect } from 'react';
import { Row, Col, CardBody } from 'reactstrap';
import { find, snakeCase, lowerCase } from 'lodash';
import { parse } from 'date-fns';
import { Button, useFetch, CardTitle, Field } from '@cordata/buffet';
import { FieldArray, useFormikContext } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import HumanInteraction from '../HumanInteraction';

const contactAttemptTypeAnswers = [
  'Face-to-Face',
  'Phone',
  'Email',
  'Social Media',
  'Mailing',
  'SMS',
].map(label => ({
  label,
  value: snakeCase(label),
}));

const yesNo = [
  'Yes',
  'No',
].map(label => ({
  label,
  value: snakeCase(label),
}));

const contactPhaseAnswers = [
  'First Contact',
  'Follow-up',
].map(label => ({
  label,
  value: snakeCase(label),
}));

const SurvivorInteraction = () => {
  const { values, setFieldValue } = useFormikContext();
  const [providerStatus, providers] = useFetch('providers/mv');
  const [genderStatus, genders] = useFetch('reference/gender', { initialValue: [] });

  useEffect(() => {
    if (values.detailObject) {
      setFieldValue('contactAttemptType', values.detailObject.contactAttemptType ? find(contactAttemptTypeAnswers, r => r.value === values.detailObject.contactAttemptType).value : undefined);
      setFieldValue('contactPhase', values.detailObject.contactPhase && find(contactPhaseAnswers, r => r.value === values.detailObject.contactPhase).value);
      setFieldValue('survivorContacted', values.detailObject.survivorContacted && find(yesNo, r => r.value === values.detailObject.survivorContacted).value);
      setFieldValue('leftFlyer', values.detailObject.leftFlyer && find(yesNo, r => r.value === values.detailObject.leftFlyer).value);
      setFieldValue('doScheduleAnother', values.detailObject.doScheduleAnother && find(yesNo, r => r.value === values.detailObject.doScheduleAnother).value);
      setFieldValue('nextInteractionDate', values.detailObject.nextInteractionDate && parse(values.detailObject.nextInteractionDate));
      setFieldValue('kitsDistributed', values.detailObject.kitsDistributed);
      setFieldValue('ftStripsDistributed', values.detailObject.ftStripsDistributed);
      setFieldValue('xyStripsDistributed', values.detailObject.xyStripsDistributed);
      setFieldValue('humanInteractions', values.detailObject.humanInteractions && values.detailObject.humanInteractions.map(hi => ({
        ...hi,
        isActive: hi.isActive || true,
      })));
    }
  }, [values.detailObject]);

  useEffect(() => {
    if (providerStatus === 'success' && values.detailObject.providers) {
      setFieldValue('responseTeam', providers.filter(p => values.detailObject.providers.includes(p.id)));
    }
  }, [providerStatus]);

  return (
    <>
      <hr />

      <CardBody>
        <CardTitle>Survivorship Interaction Details</CardTitle>

        <Row form>
          <Col sm>
            <Field
              name="contactAttemptType"
              label="What channel did you use to make contact?"
              type="radio-group"
              options={contactAttemptTypeAnswers}
              required
              inline
            />
          </Col>

          <Col sm>
            <Field
              name="contactPhase"
              label="What this a first contact or follow-up?"
              type="radio-group"
              options={contactPhaseAnswers}
              required
              inline
            />
          </Col>
        </Row>

        <Row form>
          <Col sm>
            <Field
              name="survivorContacted"
              label="Did you make contact with the individual?"
              type="radio-group"
              options={yesNo}
              required
              inline
            />
          </Col>

          <Col sm>
            <Field
              name="leftFlyer"
              label="Did you leave materials/literature?"
              type="radio-group"
              options={yesNo}
              required
              inline
            />
          </Col>

          {values.status && lowerCase(values.status.attributes.statusLabel) === 'completed' &&
            <>
              <Col sm>
                <Field
                  name="kitsDistributed"
                  type="number"
                />
              </Col>
              <Col sm>
                <Field
                  label="Number of fentanyl test strips distributed"
                  name="ftStripsDistributed"
                  type="number"
                />
              </Col>
              <Col sm>
                <Field
                  label="Number of xylazine test strips distributed"
                  name="xyStripsDistributed"
                  type="number"
                />
              </Col>
            </>
          }
        </Row>

        <Field
          name="responseTeam"
          type="select"
          options={providers}
          loading={providerStatus === 'loading'}
          getOptionValue={o => o.id}
          getOptionLabel={o => o.attributes.displayName}
          multi
        />

        <CardTitle className="mt-5">Who Else Did You Talk To?</CardTitle>

        <FieldArray
          name="humanInteractions"
          render={({ push }) => (
            <>
              {values.humanInteractions &&
              values.humanInteractions.filter(r => r.isActive).map((e, i) => (
                <React.Fragment key={i}>
                  <Button
                    size="sm"
                    outline
                    color="danger"
                    spear
                    type="button"
                    onClick={() => {
                      setFieldValue(`humanInteractions.${i}.isActive`, false);
                    }}
                  >
                    <FontAwesomeIcon icon="minus" />
                    <span className="d-none d-md-inline ml-2">Remove Human Interaction</span>
                  </Button>

                  <HumanInteraction
                    key={i}
                    data={values.humanInteractions[i]}
                    name={`humanInteractions.${i}`}
                    gender={[genderStatus, genders]}
                    setFieldValue={setFieldValue}
                  />

                  {i !== values.humanInteractions.length - 1 && <hr />}
                </React.Fragment>
              ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() => push({ isActive: true })}
              >
                <FontAwesomeIcon icon="plus" />
                <span className="d-none d-md-inline ml-2">Add Human Interaction</span>
              </Button>
            </>
          )}
        />

        <CardTitle className="mt-5">What do you want to do next?</CardTitle>

        <Row form>
          <Col sm>
            <Field
              name="doScheduleAnother"
              label="Do you want to schedule another interaction?"
              type="radio-group"
              options={yesNo}
              inline
            />
          </Col>

          {values.doScheduleAnother && values.doScheduleAnother === 'yes' && (
            <Col sm>
              <Field
                name="nextInteractionDate"
                label="Scheduled for Date"
                placeholder="Enter Next Scheduled Date"
                type="date"
              />
            </Col>
          )}
        </Row>
      </CardBody>
    </>
  );
};

export default SurvivorInteraction;
